// ============================================================================
// ChatEntityBlock — блок «О сущности» правой панели чата (контекстные чаты).
// Показывает реквизиты/статус/ключевые поля записи и бизнес-действие
// «Открыть объект». Данные — существующие RPC (listTableData/listFields/
// getRegister/getEntity), SSoT — сервер (представления ссылок и статуса).
// ============================================================================

import { useEffect, useState } from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { friendlyError } from '@/lib/errors';
import { openObjectRecord } from '@/lib/chatNavigation';
import { loadChatEntityInfo, type ChatEntityInfo } from './chatInfoData';

interface ChatEntityBlockProps {
  contextType: string;
  contextCode: string;
  contextId: string;
}

interface State {
  loading: boolean;
  info?: ChatEntityInfo;
  fromCache?: boolean;
  error?: string;
}

export function ChatEntityBlock({ contextType, contextCode, contextId }: ChatEntityBlockProps) {
  const [state, setState] = useState<State>({ loading: true });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    loadChatEntityInfo(contextType, contextCode, contextId)
      .then((r) => {
        if (alive) setState({ loading: false, info: r.info, fromCache: r.fromCache });
      })
      .catch((e) => {
        if (alive) setState({ loading: false, error: friendlyError(e) });
      });
    return () => { alive = false; };
  }, [contextType, contextCode, contextId, tick]);

  if (state.loading) {
    return (
      <div className="glass-panel rounded-xl p-3 space-y-2 animate-pulse">
        <div className="h-3 w-24 rounded bg-muted" />
        <div className="h-4 w-40 rounded bg-muted" />
        <div className="h-3 w-full rounded bg-muted" />
        <div className="h-3 w-2/3 rounded bg-muted" />
      </div>
    );
  }

  if (state.error || !state.info) {
    return (
      <div className="glass-panel rounded-xl p-3 space-y-2">
        <div className="text-[12px] text-destructive">{state.error || 'Не удалось загрузить сведения'}</div>
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setTick((v) => v + 1)}>
          <RefreshCw className="h-3 w-3 mr-1" />Повторить
        </Button>
      </div>
    );
  }

  const info = state.info;
  return (
    <div className="glass-panel rounded-xl p-3 space-y-2.5">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="shrink-0 rounded-full bg-primary/10 text-primary border border-primary/25 px-2 py-0.5 text-[10px] font-semibold">
          {info.typeLabel}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground" title={info.objectLabel}>
          {info.objectLabel}
        </span>
        <button
          type="button"
          className="shrink-0 p-1 rounded-full hover:bg-muted/50 text-muted-foreground"
          onClick={() => setTick((v) => v + 1)}
          title="Обновить сведения"
          aria-label="Обновить сведения"
        >
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>

      <div className="min-w-0">
        <div className="text-[14px] font-semibold leading-tight break-words">{info.title}</div>
        {info.status && (
          <span className="inline-block mt-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium">
            {info.status}
          </span>
        )}
      </div>

      {info.description && (
        <div className="text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-4" title={info.description}>
          {info.description}
        </div>
      )}

      {info.fields.length > 0 && (
        <div className="space-y-1 border-t border-border/60 pt-2">
          {info.fields.map((f) => (
            <div key={f.label} className="flex items-start gap-2 text-[11px] min-w-0">
              <span className="w-[44%] shrink-0 text-muted-foreground truncate" title={f.label}>{f.label}</span>
              <span className="min-w-0 flex-1 truncate" title={f.value}>{f.value}</span>
            </div>
          ))}
        </div>
      )}

      <Button
        size="sm"
        className="w-full h-8 text-[11px]"
        onClick={() => openObjectRecord(contextType, contextCode, contextId)}
      >
        <ExternalLink className="h-3.5 w-3.5 mr-1" />Открыть объект
      </Button>

      {state.fromCache && (
        <div className="text-[10px] text-muted-foreground">Показаны сохранённые данные (нет связи с сервером).</div>
      )}
    </div>
  );
}
